import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Tag } from "lucide-react";
import api from "@/lib/api";
import { normalizeOffer } from "@/lib/adapt";
import { useT } from "@/lib/i18n";
import MenuButton from "@/components/mobile/MenuButton";

const DEFAULT_CENTER = { lat: 10.776, lng: 76.654 };

export default function MobileOffers() {
  const { t } = useT();
  const nav = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      let center = DEFAULT_CENTER;
      try {
        const saved = JSON.parse(localStorage.getItem("nd_center") || "null");
        if (saved?.lat) center = { lat: saved.lat, lng: saved.lng };
      } catch (_) { /* fallback */ }

      try {
        // Try the location-scoped endpoint first
        const { data } = await api.get("/offers/nearby", {
          params: { lat: center.lat, lng: center.lng, radius: 50 },
        });
        let list = Array.isArray(data) ? data : [];
        // Fallback: if the nearby endpoint returned nothing, fetch the global list
        if (list.length === 0) {
          const { data: all } = await api.get("/offers");
          list = Array.isArray(all) ? all : [];
        }
        const normalized = list.map((o) => ({
          ...normalizeOffer(o),
          shop_name: o.shop_name,
          shop_category: o.shop_category,
          shop_id: o.shop_id,
        }));
        if (alive) setOffers(normalized);
      } catch {
        if (alive) setOffers([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="px-5 pt-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">{t("offers.hotOffers")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("offers.within10")}</p>
        </div>
        <MenuButton />
      </div>

      <div className="h-px bg-border my-5" />

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-40 rounded-2xl bg-secondary animate-pulse" />
          ))}
        </div>
      ) : offers.length === 0 ? (
        <div className="grid place-items-center py-24 text-center">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-secondary text-primary mb-4">
            <Tag className="w-7 h-7" />
          </span>
          <p className="font-display font-bold text-xl">{t("offers.none")}</p>
          <p className="text-muted-foreground text-sm mt-1">{t("offers.checkBack")}</p>
        </div>
      ) : (
        <ul className="space-y-4" data-testid="offers-list">
          {offers.map((o) => (
            <li key={o.id}>
              <button
                data-testid={`offer-${o.id}`}
                onClick={() => o.shop_id && nav(`/shops/${o.shop_id}?tab=offers#offers`)}
                className="w-full text-left rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors"
              >
                <span className="inline-block text-xs font-extrabold bg-[#E7F3E5] text-[#3B7A2E] rounded-full px-3 py-1.5">
                  {o.discount || t("offers.badge")}
                </span>
                <p className="font-display font-extrabold text-xl leading-tight mt-3">{o.title}</p>
                {o.description && (
                  <p className="text-sm text-foreground/80 mt-1.5 line-clamp-3 whitespace-pre-line">
                    {o.description}
                  </p>
                )}
                <p className="flex items-center gap-2 text-sm text-muted-foreground mt-4">
                  <Store className="w-4 h-4 text-primary" />
                  <span className="font-semibold uppercase">{o.shop_name}</span>
                  {o.shop_category && (
                    <>
                      <span className="opacity-40">·</span>
                      <span className="capitalize">{o.shop_category}</span>
                    </>
                  )}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
